import { mapMutations } from 'vuex';
import { BehaviorSubject } from 'rxjs';

import DataSource, {
    UNSELECT_ALREADY_SELECTED,
} from '@/components/Dashboard/common/WidgetSettingPanel/options/dataSource';
const MultiMetric = () =>
    import(
        '@/components/Dashboard/common/WidgetSettingPanel/reactOptions/multiMetric'
    );
const MultiBreakdown = () =>
    import(
        '@/components/Dashboard/common/WidgetSettingPanel/reactOptions/multiBreakdown'
    );

import {
    checkIsAnalyticSource,
    isBlendableSource,
    hasValidBlendSetting,
    refineChannel,
    DISPLAY_CHANNEL_NAME_CHANNELS,
    refineSourceNameWithDeprecated,
} from '@/utils/dashboard';
import { isValueExist } from '@/utils/utils';
import { getFieldLocale } from '@/utils/field';

import * as S from './style';

import {
    DESTINATION_TABLE_NAME_CONNECTORS,
    EDIT_TRANSFER,
    NAME,
    SELECT_SOURCE,
    TABLE_NAME,
} from './';

import { WIDGET_TYPES } from '@/utils/consts';
import { CUSTOM_TABLE, FULL_ARCHITECTURE } from './SelectStream';
import ArchitectureTable from './ArchitectureTable';
import tableMixin from './tableMixin';
import breakdownTranslatorMixin from './breakdownTranslatorMixin';
import CustomField from './CustomField';
import StatusInput from '@/components/Common/StatusInput';
import InfoIcon from '@svgIcons/info_line.svg';

import {
    DATA_SOURCE,
    makeIndexKey,
} from '@/components/Dashboard/common/WidgetSettingPanel';
import { FIELD_PANEL_KEY } from '@/components/Dashboard/common/WidgetSettingPanel/reactOptions/consts';
import {
    fromBreakdownPanelFormat,
    fromMetricPanelFormat,
    toBreakdownPanelFormat,
    toMetricPanelFormat,
} from './utils';

const { DATA_STERAM } = WIDGET_TYPES;

const DIMENSIONS = ['channel', 'campaign', 'adSet', 'ad'];

export const ALLOWED_ORGANIC = [
    'instagramOrganic',
    'youtubeOrganic',
    'twitterOrganic',
    'linkedinOrganic',
    'facebookOrganic',
    'tiktokOrganic',
];

export const ALLOWED_ORGANIC_V2 = [
    'instagramOrganicV2',
    'tiktokOrganicV2',
    'twitterOrganicV2',
    'facebookOrganicV2',
    'linkedinOrganicV2',
];

export const ALLOWED_PUBLIC = [
    'instagramPublic',
    'youtubePublic',
    'twitterPublic',
    'tiktokPublic',
];

export const ALLOWED_ORGANIC_PUBLIC = [
    ...ALLOWED_PUBLIC,
    ...ALLOWED_ORGANIC,
    ...ALLOWED_ORGANIC_V2,
];

const ALLOWED_CUSTOM_SOURCES = [
    'googleSpreadSheet',
    'googleAnalytics',
    'googleAnalytics4',
    'googleAnalyticsMcf',
    'amplitude',
    'appsFlyer',
    'bigQuery',
    'amazonS3',
    'youtubeAnalytics',
    'youtubeAnalyticsV2',
    // 'googleTrends',
    // 'googleKeywordPlanner',
];

export const ALLOWED_NOT_MARKETING_SOURCES = [
    ...ALLOWED_CUSTOM_SOURCES,
    ...ALLOWED_ORGANIC_PUBLIC,
];

export const REFACTORED_ORGANIC_SOURCES = [
    'linkedinOrganic',
    'instagramOrganic',
    'facebookOrganic',
    'tiktokOrganic',
    'twitterOrganic',
];

export default {
    name: 'SelectSource',
    mixins: [tableMixin, breakdownTranslatorMixin],
    props: {
        customFields: {
            type: Array,
            default() {
                return [];
            },
        },
        valueMap: {
            type: Object,
            default() {
                return {};
            },
        },
        transferName: {
            type: String,
            default: '',
        },
        customFieldOnArchitectureUser: Boolean,
        allowedNotMarketingSource: {
            type: Array,
            default() {
                return ALLOWED_NOT_MARKETING_SOURCES;
            },
        },
    },
    data() {
        return {
            secondPanelVisibility$: new BehaviorSubject(null),
        };
    },
    provide() {
        return {
            secondPanelVisibility$: this.secondPanelVisibility$,
        };
    },
    computed: {
        disabled() {
            if (this.page === EDIT_TRANSFER) {
                if (this.isAnalytic) {
                    const hasNonPublic = R.any(
                        (item) => !ALLOWED_PUBLIC.includes(item.channel),
                        this.selectedSources
                    );

                    return hasNonPublic;
                }
            }

            return false;
        },
        _adsTableSources() {
            return R.reject(
                (item) =>
                    checkIsAnalyticSource(item) &&
                    !this.allowedNotMarketingSource.includes(item.channel) &&
                    !item.hasAlternativeChannelName,
                this.adsTableSources
            );
        },
        customStreamBreakdownList() {
            return R.reject(
                R.propEq('category', 'campaignStructure'),
                this.availableBreakdownList
            );
        },
        shouldRenderStreamName() {
            const transferName = this.transferName?.toLowerCase();
            return (
                this.streamType === CUSTOM_TABLE &&
                DESTINATION_TABLE_NAME_CONNECTORS.includes(transferName)
            );
        },
        selectedListInfo() {
            return this.selectedSources.reduce(
                (acc, item) => {
                    if (acc.isAllValidBlended) {
                        acc.isAllValidBlended =
                            isBlendableSource(item) &&
                            hasValidBlendSetting(item.settings);
                    }

                    acc.channels = acc.channels.add(refineChannel(item));

                    return acc;
                },
                {
                    isAllValidBlended: true,
                    channels: new Set(),
                }
            );
        },
    },
    methods: {
        ...mapMutations('view', ['setNotiMsgs']),
        onClickPreview() {
            const { sourceList, metricList } = this;

            this.$emit('setPreviewWidget', {
                columns: R.compose(
                    R.pluck('name'),
                    R.filter(R.prop('selected'))
                )(metricList),
                adAccounts: R.filter(R.prop('selected'), sourceList),
                type: 'pivotTable',
                dimensionIds: DIMENSIONS,
                id: 'graph-panel-grid',
                filterTagList: [],
                widget_title:
                    DIMENSIONS.reduce((acc, key, index, list) => {
                        acc += getFieldLocale.call(this, key);

                        if (index < list.length - 1) {
                            acc += ' > ';
                        }

                        return acc;
                    }, '') + '*',
            });
        },
        sourceTooltipGenerator(item) {
            if (this.page === EDIT_TRANSFER) {
                if (
                    this.isAnalytic ||
                    (!this.isAnalytic && checkIsAnalyticSource(item))
                ) {
                    if (!ALLOWED_PUBLIC.includes(item.channel)) {
                        return this.$t(
                            'EXPORT_DATA.block_edit_transfer_marketing_to_custom_tooltip'
                        );
                    }
                }
            }

            if (
                checkIsAnalyticSource(item) &&
                !this.allowedNotMarketingSource.includes(item.channel) &&
                !item.hasAlternativeChannelName
            ) {
                return this.$t('EXPORT_DATA.not_allowed_custom_source_tooltip');
            }
        },
        renderTableName() {
            return (
                <S.TableNameWrapper>
                    <S.ConfigTitleWrapper>
                        <S.ConfigTitle>
                            {this.$t('EXPORT_DATA.destination_name_title')}
                        </S.ConfigTitle>

                        {this.wrapTooltip({
                            title: (
                                <S.TooltipWrapper>
                                    <InfoIcon
                                        class="info-icon-tooltip"
                                        v-view-box
                                    />
                                    {this.$t(
                                        'EXPORT_DATA.destination_name_instruction_tooltip'
                                    )}
                                </S.TooltipWrapper>
                            ),
                            visible: true,
                            content: <InfoIcon class="info-icon" v-view-box />,
                            placement: 'bottom',
                        })}
                    </S.ConfigTitleWrapper>
                    <StatusInput
                        type="input"
                        value={this.valueMap[TABLE_NAME] || ''}
                        onInput={(v) =>
                            this.$emit(
                                'setValueMap',
                                R.assoc(TABLE_NAME, v, this.valueMap)
                            )
                        }
                        errorText={
                            !this.valueMap[TABLE_NAME]
                                ? this.$t('COMMON.field_required')
                                : ''
                        }
                        errorDirection="right"
                        disabled={this.page === EDIT_TRANSFER}
                        counterVisible={false}
                        cleaner={R.replace(/[^a-z0-9_]+/g, '')}
                        dir="rtl"
                    />
                </S.TableNameWrapper>
            );
        },
        isMarketing() {
            const isMarketing =
                this.selectedSources.length > 0 &&
                !this.allowedNotMarketingSource.includes(
                    this.selectedSources[0].channel
                );
            return isMarketing;
        },
        analyticGenerateOption(item) {
            const { selectedListInfo, selectedSources } = this;

            let options = [];
            let message = this.$t(
                'NEW_DASHBOARD.change_source_mode_modal_basic_desc'
            );

            const {
                channel: curChannel,
                name,
                isSupportMultiDatasources = false,
            } = item;

            const curRefinedChannel = refineChannel(item);

            const { isAllValidBlended, channels } = selectedListInfo;
            const selectedChannels = Array.from(channels);

            if (!selectedChannels.includes(curRefinedChannel)) {
                options = [UNSELECT_ALREADY_SELECTED];
            } else {
                options = isSupportMultiDatasources
                    ? []
                    : [UNSELECT_ALREADY_SELECTED];
            }

            if (
                [
                    ...DISPLAY_CHANNEL_NAME_CHANNELS,
                    'redshift',
                    'bigQuery',
                    'googleAnalytics',
                    'googleAnalytics4',
                ].includes(curChannel)
            ) {
                if (isAllValidBlended && hasValidBlendSetting(item.settings)) {
                    options = [];
                } else {
                    options = [UNSELECT_ALREADY_SELECTED];
                    message = this.$t(
                        'NEW_DASHBOARD.change_source_mode_modal_gss_desc'
                    );
                }
            } else if (curChannel === 'airbyte') {
                if (R.any(R.propEq('name', name), selectedSources)) {
                    options = [];
                }

                if (isValueExist(options)) {
                    const { name, channelId, dataSourceName } = item;

                    message = this.$t(
                        'NEW_DASHBOARD.change_source_mode_modal_airbyte_desc',
                        {
                            dataSourceName,
                            name: `${name} | ${channelId}`,
                        }
                    );
                }
            } else {
                const isMarketingSource = !checkIsAnalyticSource(item);

                if (isMarketingSource && isAllValidBlended) {
                    options = [];
                }

                if (ALLOWED_ORGANIC_V2.includes(curRefinedChannel)) {
                    options = [];
                }
            }

            if (message) {
                message = this.$t(
                    'EXPORT_DATA.not_allowed_source_without_blend_desc'
                );
            }

            return { options, message };
        },
    },
    mounted() {
        if (
            this.page === SELECT_SOURCE &&
            !this.valueMap[TABLE_NAME] &&
            this.shouldRenderStreamName
        ) {
            this.$emit(
                'setValueMap',
                R.assoc(
                    TABLE_NAME,
                    `${this.valueMap[NAME]}_${this.cAccountId}`,
                    this.valueMap
                )
            );
        }
    },

    render() {
        return (
            <S.SelectSourceWrapper>
                {this.shouldRenderStreamName && this.renderTableName()}
                {this.page === EDIT_TRANSFER && (
                    <S.ConfigTitle>
                        {this.$t('EXPORT_DATA.form_label_data_source')}
                    </S.ConfigTitle>
                )}
                <DataSource
                    currentList={this.sourceList}
                    rawList={
                        this.streamType === FULL_ARCHITECTURE
                            ? this._adsTableSources
                            : this._tableAdAccounts
                    }
                    translator={refineSourceNameWithDeprecated.bind(this)}
                    indexKey={makeIndexKey(DATA_SOURCE)}
                    compKey={DATA_SOURCE}
                    localeKey="source"
                    onUpdate={(v) => this.$emit('setSourceList', v)}
                    showAddConnection={false}
                    isAnalytic={this.isAnalytic}
                    selectedList={this.selectedSources}
                    disabled={this.disabled}
                    disabledTooltipGenerator={this.sourceTooltipGenerator}
                    widgetType={DATA_STERAM}
                    onResetTableValues={this.resetTableValues}
                    analyticGenerateOption={this.analyticGenerateOption}
                />
                {this.streamType === FULL_ARCHITECTURE
                    ? [
                          <ArchitectureTable
                              {...{
                                  attrs: {
                                      metricList: this.metricList,
                                      onUpdateMetricWithCheck:
                                          this.onUpdateMetricWithCheck,
                                      isAnalytic: this.isAnalytic,
                                      selectedSources: this.selectedSources,
                                      selectedArchLevels:
                                          this.selectedArchLevels,
                                      page: this.page,
                                  },
                                  on: { ...this.$listeners },
                              }}
                          />,
                          this.customFieldOnArchitectureUser ? (
                              <CustomField
                                  customFields={this.customFields}
                                  categoryLocaleMap={this.categoryLocaleMap}
                                  availableMetricList={this.availableMetricList}
                                  translator={this.refineMetric}
                                  isMarketing={true}
                                  streamType={this.streamType}
                                  onSetCustomFields={(v) =>
                                      this.$emit('setCustomFields', v)
                                  }
                                  onSetHasCustomFieldChanged={(v) =>
                                      this.$emit('setHasCustomFieldChanged', v)
                                  }
                              />
                          ) : null,
                      ]
                    : [
                          /**
                           * [TODO]
                           * - 기존 rawList 확인
                           */
                          <MultiBreakdown
                              panelKey={FIELD_PANEL_KEY.DATASTREAM_BREAKDOWN()}
                              currentList={this.breakdownList.map(
                                  toBreakdownPanelFormat
                              )}
                              canEditCustomBreakdown={false}
                              selectedSource={this.selectedSources}
                              currentBreakdownIds={this.currentBreakdownIds}
                              isAnalytic={this.isAnalytic}
                              datasources={this.selectedSources.map(
                                  ({ assetId, channel, dataSourcePrefix }) => ({
                                      assetId,
                                      channel,
                                      dataSourcePrefix,
                                  })
                              )}
                              isForDatastream={true}
                              hideCustomTab={true}
                              onUpdate={(v) => {
                                  const updated = v.map(
                                      fromBreakdownPanelFormat
                                  );
                                  this.$emit('setBreakdownList', updated);
                              }}
                              disabled={this.disabled}
                              panelTitle={this.$t(
                                  'EXPORT_DATA.dimensions_title'
                              )}
                              optionTitle={this.$t(
                                  'EXPORT_DATA.dimensions_title'
                              )}
                              //   rawList={
                              //     this.streamType === CUSTOM_TABLE &&
                              //     !this.isAnalytic
                              //         ? this.customStreamBreakdownList
                              //         : this.availableBreakdownList
                              // }
                          />,
                          <MultiMetric
                              panelKey={FIELD_PANEL_KEY.DATASTREAM_METRIC()}
                              currentList={this.metricList.map(
                                  toMetricPanelFormat
                              )}
                              canEditCustomMetric={false}
                              selectedSource={this.selectedSources}
                              currentBreakdownIds={this.currentBreakdownIds}
                              isAnalytic={this.isAnalytic}
                              isForDatastream={true}
                              hideCustomTab={true}
                              onUpdate={(v) => {
                                  const updated = v.map(fromMetricPanelFormat);
                                  this.onUpdateMetricWithCheck(updated);
                              }}
                              disabled={this.disabled}
                          />,
                          this.isCustomFieldUser ? (
                              <CustomField
                                  customFields={this.customFields}
                                  categoryLocaleMap={this.categoryLocaleMap}
                                  availableMetricList={this.availableMetricList}
                                  availableBreakdownList={
                                      this.availableBreakdownList
                                  }
                                  translator={this.refineMetric}
                                  isMarketing={this.isMarketing()}
                                  selectedSources={this.selectedSources}
                                  onSetCustomFields={(v) =>
                                      this.$emit('setCustomFields', v)
                                  }
                                  onSetHasCustomFieldChanged={(v) =>
                                      this.$emit('setHasCustomFieldChanged', v)
                                  }
                              />
                          ) : null,
                      ]}
                {/* <S.PreviewButton onClick={this.onClickPreview}>
                    {this.$t('EXPORT_DATA.preview_button_title')}
                </S.PreviewButton> */}
            </S.SelectSourceWrapper>
        );
    },
};
